<!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import camelcaseKeys from 'camelcase-keys';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import AgentRelationshipsAPI from 'dashboard/api/agentRelationships';
import AnalyticsEventsAPI from 'dashboard/api/analyticsEvents';
import ImovelAPI from 'dashboard/api/imoveis';
import { useShopWindowAccess } from 'dashboard/composables/useShopWindowAccess';

import AgentArea from 'dashboard/components-next/AgentArea.vue';
import AllReadyDialog from 'dashboard/components-next/AllReadyDialog.vue';
import CreateShopWindowDialog from 'dashboard/components-next/CreateShopWindowDialog.vue';
import GoToAgentAreaButton from 'dashboard/components-next/GoToAgentAreaButton.vue';
import KeyHolderDrawer from 'dashboard/components-next/KeyHolderDrawer.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import RedeAlert from 'dashboard/components-next/RedeAlert.vue';
import ShopWindowButton from 'dashboard/components-next/ShopWindowButton.vue';
import ShopWindowDialog from 'dashboard/components-next/ShopWindowDialog.vue';
import ShopWindowOnboardingContainer from 'dashboard/components-next/ShopWindowOnboardingContainer.vue';

const KEY_HOLDER_DRAWER_COMPONENT = 'KeyHolderDrawer.39d56390ced7df7f.js';
const KEY_HOLDER_LOCK_TITLE = 'O imóvel já possui um corretor responsável';
const KEY_HOLDER_VALIDATION_TITLE = 'Validação de chaves necessária';
const KEY_HOLDER_DRAWER_ROLE = 'agente_rede';
const KEY_HOLDER_DRAWER_ANCHOR = 'bottom/right';

const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');

const {
  featureEnabled,
  keysWithAgent,
  keyHolderValidationEnabled,
  keyHolderEntries,
  blockedByKeyHolder,
  blockedHolderName,
  checking: checkingEntryAccess,
  canCreateShopWindow,
  ensureFeatureFlag,
  checkEntryAccess,
  marketplaceUrl,
} = useShopWindowAccess();

const isLoading = ref(true);
const isSaving = ref(false);
const loadError = ref('');
const profile = ref(null);
const relationships = ref([]);
const imoveis = ref([]);
const selectedRelationshipId = ref(null);
const selectedImovelIds = ref(new Set());
const showShopWindowDialog = ref(false);
const shopWindowDialogState = ref('pick');
const pendingImovel = ref(null);
const showCreateDialog = ref(false);
const createDialogState = ref('form');
const createDialogMode = ref('create');
const showAllReadyDialog = ref(false);
const showOnboarding = ref(true);
const showUnpublishConfirm = ref(false);

const keyHolderDrawer = reactive({
  show: false,
  title: KEY_HOLDER_LOCK_TITLE,
  description: '',
  holderName: '',
  imovel: null,
  reason: 'responsible_agent',
  role: KEY_HOLDER_DRAWER_ROLE,
  currentProfileRole: '',
  validationEnabled: false,
  keysWithAgent: false,
  blockedByKeyHolder: false,
  source: 'dashboard_corretor_area',
});

const normalize = value =>
  String(value || '')
    .trim()
    .toLowerCase();

const agentCode = computed(
  () => profile.value?.agentCode || currentUser.value?.id?.toString() || ''
);
const agentName = computed(
  () => profile.value?.agentName || currentUser.value?.name || 'Corretor'
);
const agentEmail = computed(
  () => profile.value?.agentEmail || currentUser.value?.email || ''
);
const agencyBrand = computed(() => profile.value?.agencyBrand || {});
const agencyName = computed(
  () =>
    agencyBrand.value?.nomeFantasia || agencyBrand.value?.nome_fantasia || ''
);
const agencyLogo = computed(
  () => agencyBrand.value?.logoUrl || agencyBrand.value?.logo_url || ''
);
const agencySite = computed(
  () => agencyBrand.value?.siteUrl || agencyBrand.value?.site_url || ''
);
const businessContext = computed(() => {
  const contexts = profile.value?.businessContextsServed || [];
  return contexts.includes('comprar') && !contexts.includes('alugar')
    ? 'comprar'
    : 'alugar';
});
const currentProfileRole = computed(
  () => profile.value?.role || currentUser.value?.role || ''
);

const currentAgentIdentifiers = computed(() =>
  [agentCode.value, agentEmail.value, agentName.value]
    .map(normalize)
    .filter(Boolean)
);

const activeRelationship = computed(() => {
  if (!relationships.value.length) return null;
  return (
    relationships.value.find(
      item => item.id === selectedRelationshipId.value
    ) || relationships.value[0]
  );
});

const metadata = computed(() => activeRelationship.value?.metadata || {});
const toArray = value => {
  if (Array.isArray(value)) return value;
  if (value == null || value === '') return [];
  return [value];
};

const highlightedIds = relationship =>
  toArray(
    relationship?.metadata?.highlightedImovelIds ||
      relationship?.metadata?.highlighted_imovel_ids ||
      []
  )
    .map(Number)
    .filter(Boolean);

const currentShopWindowId = computed(
  () =>
    metadata.value.shopwindowId ||
    metadata.value.shopwindow_id ||
    profile.value?.shopwindowId
);
const published = computed(
  () =>
    metadata.value.published === true &&
    activeRelationship.value?.status !== 'blocked'
);
const selectedIds = computed(() =>
  Array.from(selectedImovelIds.value).map(Number).filter(Boolean)
);
const currentMarketplaceUrl = computed(
  () =>
    metadata.value.marketplaceUrl ||
    metadata.value.marketplace_url ||
    profile.value?.marketplaceUrl ||
    `/lais/vitrine/${encodeURIComponent(agentCode.value || 'corretor')}/imovel/preview/${businessContext.value}`
);
const selectedImoveis = computed(() =>
  imoveis.value.filter(imovel => selectedImovelIds.value.has(Number(imovel.id)))
);

const humanizeShopWindowId = value => {
  const cleaned = String(value || '')
    .replace(/^sw-\d+-/, '')
    .replace(/^shopwindow-/, '')
    .replace(/[-_]+/g, ' ')
    .trim();

  if (!cleaned) return 'Vitrine do corretor';
  return `${cleaned.charAt(0).toUpperCase()}${cleaned.slice(1)}`;
};

const shopWindowName = computed(() =>
  humanizeShopWindowId(currentShopWindowId.value)
);
const shopWindows = computed(() =>
  currentShopWindowId.value
    ? [
        {
          id: currentShopWindowId.value,
          name: shopWindowName.value,
          imovel_count: selectedIds.value.length,
          published: published.value,
          marketplace_url: currentMarketplaceUrl.value,
        },
      ]
    : []
);

const relationshipOptions = computed(() =>
  relationships.value.map(item => ({
    id: item.id,
    label: `${item.agentCode || item.agent_code || 'corretor'} · contato #${item.contactId || item.contact_id}`,
    status: item.status,
  }))
);

const selectedEntryAccess = computed(() => {
  const entry = keyHolderEntries.value[0] || {};
  const entryMetadata = entry.metadata || {};
  return {
    ...entry,
    kind: entry.kind || 'pending',
    keys_with_agent: keysWithAgent.value,
    key_holder_validation_enabled: keyHolderValidationEnabled.value,
    access_label: entry.access_label,
    helper_info: entry.helper_info,
    details: Array.isArray(entry.details) ? entry.details : [],
    conditions: entry.instructions || entryMetadata.conditions || '',
    metadata: entryMetadata,
  };
});

const agentAreaImovel = computed(() => ({
  ...(selectedImoveis.value[0] || imoveis.value[0] || {}),
  entry_access: selectedEntryAccess.value,
}));

const keyHolderBlock = computed(() => ({
  blocked: blockedByKeyHolder.value,
  holderName: blockedHolderName.value,
  validationEnabled: keyHolderValidationEnabled.value,
  role: KEY_HOLDER_DRAWER_ROLE,
}));

const operationalSuggestions = computed(() => {
  const suggestions = [];
  if (!currentShopWindowId.value) {
    suggestions.push({
      id: 'create-shopwindow',
      title: 'Criar vitrine',
      body: 'A vitrine usa o relacionamento ativo do corretor e os imóveis selecionados abaixo.',
    });
  }
  if (!selectedIds.value.length && imoveis.value.length) {
    suggestions.push({
      id: 'select-listings',
      title: 'Selecionar imóveis',
      body: 'Adicione pelo menos um imóvel para liberar publicação pública.',
    });
  }
  if (published.value) {
    suggestions.push({
      id: 'review-public-preview',
      title: 'Revisar preview',
      body: 'A vitrine está publicada; confira o link público antes de compartilhar.',
    });
  }
  return suggestions;
});

const track = async (name, properties = {}) => {
  try {
    await AnalyticsEventsAPI.create({
      account_id: accountId.value,
      name,
      source: 'web',
      properties: {
        agent_code: agentCode.value,
        relationship_id: activeRelationship.value?.id,
        shopwindow_id: currentShopWindowId.value,
        ...properties,
      },
    });
  } catch {
    // Tracking must never block the broker flow.
  }
};

const slugify = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 48);

function buildMarketplaceUrl(
  firstImovelId = selectedIds.value[0] || 'preview'
) {
  return `/lais/vitrine/${encodeURIComponent(agentCode.value || 'corretor')}/imovel/${firstImovelId}/${businessContext.value}`;
}

const buildShopWindowId = name => {
  const base = slugify(name || shopWindowName.value || 'vitrine');
  return `sw-${activeRelationship.value?.id || agentCode.value || 'corretor'}-${base || 'vitrine'}`;
};

const extractError = error =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  'Não foi possível concluir a operação agora.';

const isCurrentAgentRelationship = relationship => {
  const relationshipCode = normalize(
    relationship?.agentCode || relationship?.agent_code
  );
  return currentAgentIdentifiers.value.includes(relationshipCode);
};

const pickInitialRelationship = records => {
  const own = records.find(isCurrentAgentRelationship);
  return own?.id || records[0]?.id || null;
};

const hydrateSelectedImoveis = relationship => {
  selectedImovelIds.value = new Set(highlightedIds(relationship));
};

watch(activeRelationship, relationship => {
  hydrateSelectedImoveis(relationship);
});

const normalizeImoveis = data =>
  camelcaseKeys(
    Array.isArray(data?.payload) ? data.payload : data?.payload || [],
    {
      deep: true,
    }
  );

const loadData = async () => {
  isLoading.value = true;
  loadError.value = '';

  try {
    const [profileResponse, relationshipsResponse, imoveisResponse] =
      await Promise.all([
        AgentRelationshipsAPI.me(),
        AgentRelationshipsAPI.list(),
        ImovelAPI.get({ per_page: 24, apenas_disponiveis: true }),
        ensureFeatureFlag(),
      ]);

    profile.value = camelcaseKeys(profileResponse.data || {}, { deep: true });
    keysWithAgent.value = Boolean(profile.value?.keysWithAgent);
    keyHolderValidationEnabled.value = Boolean(
      profile.value?.keyHolderValidationEnabled
    );
    relationships.value = camelcaseKeys(relationshipsResponse.data || [], {
      deep: true,
    });
    imoveis.value = normalizeImoveis(imoveisResponse.data);
    selectedRelationshipId.value = pickInitialRelationship(relationships.value);
    hydrateSelectedImoveis(activeRelationship.value);
    await track('listing_agents_section_button_clicked', {
      click_origin: 'dashboard_corretor_area',
    });
  } catch (error) {
    loadError.value = extractError(error);
  } finally {
    isLoading.value = false;
  }
};

const replaceRelationship = relationship => {
  const exists = relationships.value.some(item => item.id === relationship.id);
  relationships.value = exists
    ? relationships.value.map(item =>
        item.id === relationship.id ? relationship : item
      )
    : [relationship, ...relationships.value];
  selectedRelationshipId.value = relationship.id;
};

const patchShopWindow = async (payload, successMessage, eventName) => {
  if (!activeRelationship.value?.id) {
    useAlert(
      'Não encontramos um relacionamento de corretor ativo para salvar a vitrine.'
    );
    return null;
  }

  isSaving.value = true;
  loadError.value = '';

  try {
    const { data } = await AgentRelationshipsAPI.shopwindow(
      activeRelationship.value.id,
      payload
    );
    const relationship = camelcaseKeys(data?.relationship || {}, {
      deep: true,
    });
    replaceRelationship(relationship);
    if (data?.agent_profile) {
      profile.value = camelcaseKeys(data.agent_profile, { deep: true });
    }
    useAlert(successMessage);
    await track(eventName, {
      published: payload.published,
      highlighted_imovel_ids: payload.highlighted_imovel_ids,
    });
    return relationship;
  } catch (error) {
    const message = extractError(error);
    loadError.value = message;
    useAlert(message);
    return null;
  } finally {
    isSaving.value = false;
  }
};

const currentPublishedParam = () => {
  if (typeof metadata.value.published === 'boolean')
    return metadata.value.published;
  return undefined;
};

const buildPayload = ({ name, published: publishedValue } = {}) => {
  const payload = {
    shopwindow_id: name
      ? buildShopWindowId(name)
      : currentShopWindowId.value || buildShopWindowId(shopWindowName.value),
    marketplace_url: buildMarketplaceUrl(),
    highlighted_imovel_ids: selectedIds.value,
  };
  const resolvedPublished =
    typeof publishedValue === 'boolean'
      ? publishedValue
      : currentPublishedParam();

  if (typeof resolvedPublished === 'boolean') {
    payload.published = resolvedPublished;
  }

  return payload;
};

const openCreateDialog = (mode = 'create') => {
  if (!activeRelationship.value?.id) {
    useAlert('A vitrine precisa de um relacionamento de corretor ativo.');
    return;
  }

  createDialogMode.value = mode;
  createDialogState.value = 'form';
  showCreateDialog.value = true;
  if (mode === 'create') {
    track('create_new_shopwindow_button_clicked', {
      click_origin: 'dashboard_corretor_area',
    });
  }
};

const submitCreateDialog = async name => {
  const relationship = await patchShopWindow(
    buildPayload({ name }),
    createDialogMode.value === 'edit'
      ? 'Vitrine atualizada.'
      : 'Vitrine criada.',
    createDialogMode.value === 'edit'
      ? 'shopwindow_updated'
      : 'shopwindow_created'
  );

  createDialogState.value = relationship ? 'success' : 'error';
  if (relationship) {
    await track('add_to_shopwindow_success_page_view', {
      flow: createDialogMode.value,
    });
  }
};

const saveDraft = () =>
  patchShopWindow(
    buildPayload(),
    'Seleção da vitrine salva.',
    'shopwindow_saved'
  );

const publishShopWindow = async () => {
  if (!selectedIds.value.length) {
    useAlert('Adicione pelo menos um imóvel antes de publicar a vitrine.');
    return;
  }

  const relationship = await patchShopWindow(
    buildPayload({ published: true }),
    'Vitrine publicada.',
    'shopwindow_published'
  );
  if (relationship) showAllReadyDialog.value = true;
};

const requestUnpublishShopWindow = () => {
  showUnpublishConfirm.value = true;
};

const unpublishShopWindow = async () => {
  const relationship = await patchShopWindow(
    buildPayload({ published: false }),
    'Vitrine despublicada.',
    'shopwindow_unpublished'
  );
  if (relationship) showUnpublishConfirm.value = false;
  return relationship;
};

const openKeyHolderDrawer = (imovel, accessResult = {}) => {
  const blockedByResponsible = Boolean(accessResult.blockedByKeyHolder);
  const validationEnabled =
    accessResult.keyHolderValidationEnabled ??
    keyHolderValidationEnabled.value ??
    false;
  const hasKeysWithAgent =
    accessResult.keysWithAgent ?? keysWithAgent.value ?? false;

  keyHolderDrawer.imovel = imovel || null;
  keyHolderDrawer.holderName =
    accessResult.blockedHolderName || blockedHolderName.value || '';
  keyHolderDrawer.role = accessResult.role || KEY_HOLDER_DRAWER_ROLE;
  keyHolderDrawer.currentProfileRole = currentProfileRole.value || '';
  keyHolderDrawer.validationEnabled = Boolean(validationEnabled);
  keyHolderDrawer.keysWithAgent = Boolean(hasKeysWithAgent);
  keyHolderDrawer.blockedByKeyHolder = blockedByResponsible;
  keyHolderDrawer.source =
    accessResult.source ||
    accessResult.clickOrigin ||
    'dashboard_corretor_area';
  keyHolderDrawer.reason = blockedByResponsible
    ? 'responsible_agent'
    : 'validation_required';
  keyHolderDrawer.title = blockedByResponsible
    ? KEY_HOLDER_LOCK_TITLE
    : KEY_HOLDER_VALIDATION_TITLE;
  keyHolderDrawer.description = blockedByResponsible
    ? 'Este imóvel está com chave atribuída a outro responsável. A vitrine fica bloqueada para evitar conflito na publicação.'
    : 'Este imóvel ainda não liberou o fluxo de chave com corretor para publicação em vitrine.';
  keyHolderDrawer.show = true;
  track('key_holder_lock_drawer_viewed', {
    house_id: imovel?.id,
    drawer_component: KEY_HOLDER_DRAWER_COMPONENT,
    drawer_anchor: KEY_HOLDER_DRAWER_ANCHOR,
    drawer_title: keyHolderDrawer.title,
    role: keyHolderDrawer.role,
    current_profile_role: currentProfileRole.value,
    key_holder_validation_enabled: Boolean(validationEnabled),
    keys_with_agent: Boolean(hasKeysWithAgent),
    blocked_by_key_holder: blockedByResponsible,
    blocked_holder_name_present: Boolean(keyHolderDrawer.holderName),
    key_holder_role_matrix_visible: true,
    key_holder_audit_ledger_visible: true,
    reason: keyHolderDrawer.reason,
    source: keyHolderDrawer.source,
  });
};

const validateImovelForShopWindow = async imovel => {
  const accessResult = await checkEntryAccess(imovel.id, profile.value || {});
  if (
    accessResult.keyHolderValidationEnabled &&
    accessResult.blockedByKeyHolder
  ) {
    openKeyHolderDrawer(imovel, accessResult);
    return false;
  }

  if (!featureEnabled.value && !accessResult.keysWithAgent) {
    openKeyHolderDrawer(imovel, accessResult);
    return false;
  }

  return true;
};

const addImovelToSelection = async imovel => {
  if (selectedImovelIds.value.has(Number(imovel.id))) return true;
  const allowed = await validateImovelForShopWindow(imovel);
  if (!allowed) return false;

  selectedImovelIds.value = new Set([
    ...selectedImovelIds.value,
    Number(imovel.id),
  ]);
  await track('shopwindow_listing_added', { house_id: imovel.id });
  return true;
};

const removeImovelFromSelection = async imovel => {
  const next = new Set(selectedImovelIds.value);
  next.delete(Number(imovel.id));
  selectedImovelIds.value = next;
  await track('shopwindow_listing_removed', { house_id: imovel.id });
};

const toggleImovel = async imovel => {
  if (selectedImovelIds.value.has(Number(imovel.id))) {
    await removeImovelFromSelection(imovel);
    return;
  }
  await addImovelToSelection(imovel);
};

const openAddDialog = imovel => {
  pendingImovel.value = imovel;
  shopWindowDialogState.value = currentShopWindowId.value ? 'pick' : 'empty';
  showShopWindowDialog.value = true;
  track('add_to_shopwindow_intent_page_view', {
    house_id: imovel?.id,
    flow: 'dashboard_corretor_area',
  });
};

const selectShopWindow = async () => {
  if (!pendingImovel.value) return;
  shopWindowDialogState.value = 'loading';
  const added = await addImovelToSelection(pendingImovel.value);
  if (!added) {
    shopWindowDialogState.value = 'blocked';
    return;
  }

  const relationship = await saveDraft();
  shopWindowDialogState.value = relationship ? 'success' : 'error';
};

const closeShopWindowDialog = () => {
  showShopWindowDialog.value = false;
  pendingImovel.value = null;
  shopWindowDialogState.value = 'pick';
};

const propertyTitle = imovel =>
  imovel.titulo ||
  [imovel.tipo, imovel.bairro, imovel.cidade].filter(Boolean).join(' em ');

const formatMoney = value =>
  value
    ? new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0,
      }).format(value)
    : 'Preço sob consulta';

const openPublicPreview = () => {
  if (!published.value) {
    useAlert(
      'Esta vitrine ainda é um rascunho. O link público pode estar indisponível até a publicação.'
    );
  }
  window.open(currentMarketplaceUrl.value, '_blank', 'noopener');
};

const goToVisits = () => {
  router.push(frontendURL(`accounts/${accountId.value}/visitas/new`));
};

const goToImoveis = () => {
  router.push(frontendURL(`accounts/${accountId.value}/imoveis`));
};

onMounted(loadData);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
  <section class="flex h-full flex-col overflow-hidden bg-[#fcfaf6]">
    <LaisHeader
      title="Área do corretor"
      helper="Controle a vitrine, chaves e preview público com os dados reais da conta."
      title-badge="Vitrine"
    >
      <template #actions>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :is-loading="isLoading"
          @click="loadData"
        >
          Atualizar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="currentShopWindowId"
          variant="ghost"
          icon="i-lucide-external-link"
          @click="openPublicPreview"
        >
          Abrir preview
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <div class="flex-1 overflow-auto px-4 py-4 sm:px-6 lg:px-10 lg:py-6">
      <div
        v-if="isLoading"
        class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px]"
      >
        <div class="h-64 animate-pulse rounded-lais bg-white" />
        <div class="h-64 animate-pulse rounded-lais bg-white" />
      </div>

      <LaisCard v-else-if="loadError" title="Não foi possível carregar">
        <p class="text-[13px] leading-6 text-rose-700">{{ loadError }}</p>
        <template #footer>
          <LaisPrimaryButton icon="i-lucide-refresh-cw" @click="loadData">
            Tentar novamente
          </LaisPrimaryButton>
        </template>
      </LaisCard>

      <div v-else class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div class="grid gap-5">
          <LaisCard title="Perfil e vínculo">
            <div
              v-if="agencyName"
              class="mb-4 flex items-center gap-3 rounded-lais border border-lais-border bg-white px-3 py-3"
            >
              <img
                v-if="agencyLogo"
                :src="agencyLogo"
                :alt="agencyName"
                class="size-11 shrink-0 rounded-lais border border-lais-border object-cover"
              />
              <div
                v-else
                class="flex size-11 shrink-0 items-center justify-center rounded-lais border border-lais-border bg-lais-primary-50 text-[16px] font-bold text-lais-primary"
              >
                {{ agencyName.slice(0, 1).toUpperCase() }}
              </div>
              <div class="min-w-0">
                <p class="text-[12px] font-semibold text-lais-text-muted">
                  Imobiliária
                </p>
                <a
                  v-if="agencySite"
                  :href="agencySite"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="break-words text-[14px] font-semibold text-lais-text hover:text-lais-link"
                >
                  {{ agencyName }}
                </a>
                <p
                  v-else
                  class="break-words text-[14px] font-semibold text-lais-text"
                >
                  {{ agencyName }}
                </p>
              </div>
            </div>
            <div
              class="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(220px,320px)]"
            >
              <div class="min-w-0">
                <p class="text-[18px] font-semibold text-lais-text">
                  {{ agentName }}
                </p>
                <p class="mt-1 break-all text-[13px] text-lais-text-muted">
                  {{ agentEmail || 'E-mail não informado' }}
                </p>
                <div class="mt-3 flex flex-wrap gap-2 text-[12px]">
                  <span
                    class="rounded-full bg-lais-mint/20 px-3 py-1 font-semibold text-[#178565]"
                  >
                    {{
                      featureEnabled || keysWithAgent
                        ? 'Vitrine liberada'
                        : 'Aguardando chave/feature'
                    }}
                  </span>
                  <span
                    class="rounded-full bg-white px-3 py-1 font-semibold text-lais-text ring-1 ring-lais-border"
                  >
                    {{ published ? 'Publicado' : 'Rascunho' }}
                  </span>
                </div>
              </div>

              <div>
                <label
                  class="text-[12px] font-semibold text-lais-text-muted"
                  for="relationship-select"
                >
                  Relacionamento ativo
                </label>
                <select
                  id="relationship-select"
                  v-model.number="selectedRelationshipId"
                  class="mt-1 w-full rounded-lais border border-lais-border bg-white px-3 py-2 text-[13px]"
                  :disabled="relationshipOptions.length <= 1"
                >
                  <option v-if="!relationshipOptions.length" :value="null">
                    Nenhum vínculo encontrado
                  </option>
                  <option
                    v-for="relationship in relationshipOptions"
                    :key="relationship.id"
                    :value="relationship.id"
                  >
                    {{ relationship.label }} · {{ relationship.status }}
                  </option>
                </select>
              </div>
            </div>
          </LaisCard>

          <AgentArea
            :imovel="agentAreaImovel"
            :agent="{
              name: agentName,
              code: agentCode,
              contact_url: currentMarketplaceUrl,
            }"
            :suggestions="operationalSuggestions"
            :is-loading="checkingEntryAccess"
            :error-message="loadError"
            :key-holder-block="keyHolderBlock"
            @schedule-visit="goToVisits"
            @register-visit="goToVisits"
            @contact-agent="openPublicPreview"
            @refresh="loadData"
            @tab-change="
              payload =>
                track('listing_agents_section_tab_clicked', {
                  agents_section_tab: payload.tab,
                  click_origin: payload.source,
                })
            "
            @opened="
              payload =>
                track('listing_agents_section_button_clicked', {
                  click_origin: payload.source,
                })
            "
            @open-key-holder="
              payload => openKeyHolderDrawer(agentAreaImovel, payload)
            "
          />

          <ShopWindowOnboardingContainer
            v-if="showOnboarding && !currentShopWindowId"
            @create-first="openCreateDialog('create')"
            @dismiss="showOnboarding = false"
            @viewed="payload => track('shopwindow_onboarding_viewed', payload)"
            @continue="
              payload => track('shopwindow_onboarding_continue', payload)
            "
            @action-button-clicked="
              payload =>
                track('shopwindow_onboarding_action_button_clicked', payload)
            "
          />

          <LaisCard title="Imóveis disponíveis">
            <div
              v-if="!imoveis.length"
              class="rounded-lais border border-dashed border-lais-border bg-white px-4 py-8 text-center"
            >
              <span
                class="i-lucide-house-plus mx-auto block size-8 text-lais-text-muted"
                aria-hidden="true"
              />
              <p class="mt-2 text-[14px] font-semibold text-lais-text">
                Nenhum imóvel disponível
              </p>
              <p class="mt-1 text-[13px] text-lais-text-muted">
                Cadastre ou importe imóveis para montar a vitrine do corretor.
              </p>
              <LaisPrimaryButton
                class="mt-4"
                icon="i-lucide-building"
                @click="goToImoveis"
              >
                Ir para empreendimentos
              </LaisPrimaryButton>
            </div>

            <div v-else class="grid gap-3 md:grid-cols-2">
              <article
                v-for="imovel in imoveis"
                :key="imovel.id"
                class="grid gap-3 rounded-lais border border-lais-border bg-white p-3"
              >
                <div class="flex gap-3">
                  <div
                    class="grid size-20 shrink-0 place-items-center overflow-hidden rounded-lais bg-lais-bg-soft"
                  >
                    <img
                      v-if="
                        imovel.midias?.[0]?.url ||
                        typeof imovel.midias?.[0] === 'string'
                      "
                      :src="imovel.midias?.[0]?.url || imovel.midias?.[0]"
                      :alt="propertyTitle(imovel)"
                      class="h-full w-full object-cover"
                    />
                    <span
                      v-else
                      class="i-lucide-image size-7 text-lais-text-muted"
                      aria-hidden="true"
                    />
                  </div>
                  <div class="min-w-0">
                    <p
                      class="line-clamp-2 text-[14px] font-semibold text-lais-text"
                    >
                      {{ propertyTitle(imovel) }}
                    </p>
                    <p class="mt-1 text-[12px] text-lais-text-muted">
                      {{
                        [imovel.bairro, imovel.cidade]
                          .filter(Boolean)
                          .join(', ') || 'Localização não informada'
                      }}
                    </p>
                    <p class="mt-1 text-[13px] font-semibold text-lais-text">
                      {{
                        formatMoney(imovel.valorAluguel || imovel.valorVenda)
                      }}
                    </p>
                  </div>
                </div>

                <RedeAlert
                  :imovel="imovel"
                  :business-context="businessContext"
                />

                <div class="flex flex-wrap items-center justify-between gap-2">
                  <button
                    type="button"
                    class="text-[12px] font-semibold text-lais-primary underline"
                    @click="toggleImovel(imovel)"
                  >
                    {{
                      selectedImovelIds.has(Number(imovel.id))
                        ? 'Remover da seleção'
                        : 'Selecionar'
                    }}
                  </button>
                  <ShopWindowButton
                    :active="selectedImovelIds.has(Number(imovel.id))"
                    :disabled="!activeRelationship"
                    :is-loading="
                      checkingEntryAccess && pendingImovel?.id === imovel.id
                    "
                    @click="openAddDialog(imovel)"
                  />
                </div>
              </article>
            </div>
          </LaisCard>
        </div>

        <aside class="grid content-start gap-5">
          <LaisCard title="Vitrine">
            <div class="space-y-4 text-[13px] leading-6 text-lais-text-muted">
              <div>
                <p class="text-[16px] font-semibold text-lais-text">
                  {{
                    currentShopWindowId ? shopWindowName : 'Sem vitrine criada'
                  }}
                </p>
                <p class="break-all">{{ currentMarketplaceUrl }}</p>
              </div>

              <div class="grid grid-cols-2 gap-2">
                <div
                  class="rounded-lais bg-white px-3 py-2 ring-1 ring-lais-border"
                >
                  <p
                    class="text-[11px] font-semibold uppercase text-lais-text-muted"
                  >
                    Imóveis
                  </p>
                  <p class="text-[20px] font-semibold text-lais-text">
                    {{ selectedIds.length }}
                  </p>
                </div>
                <div
                  class="rounded-lais bg-white px-3 py-2 ring-1 ring-lais-border"
                >
                  <p
                    class="text-[11px] font-semibold uppercase text-lais-text-muted"
                  >
                    Status
                  </p>
                  <p class="text-[14px] font-semibold text-lais-text">
                    {{ published ? 'Publicado' : 'Rascunho' }}
                  </p>
                </div>
              </div>

              <div
                v-if="!activeRelationship"
                class="rounded-lais border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900"
              >
                Nenhum corretor ativo foi encontrado para publicar a vitrine.
              </div>

              <div class="flex flex-wrap gap-2">
                <LaisPrimaryButton
                  icon="i-lucide-store"
                  :disabled="!activeRelationship"
                  @click="
                    openCreateDialog(currentShopWindowId ? 'edit' : 'create')
                  "
                >
                  {{ currentShopWindowId ? 'Editar vitrine' : 'Criar vitrine' }}
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  variant="ghost"
                  icon="i-lucide-save"
                  :is-loading="isSaving"
                  :disabled="!currentShopWindowId"
                  @click="saveDraft"
                >
                  Salvar
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  v-if="!published"
                  variant="ghost"
                  icon="i-lucide-upload-cloud"
                  :is-loading="isSaving"
                  :disabled="!currentShopWindowId || !canCreateShopWindow"
                  @click="publishShopWindow"
                >
                  Publicar
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  v-else
                  variant="ghost"
                  icon="i-lucide-eye-off"
                  :is-loading="isSaving"
                  @click="requestUnpublishShopWindow"
                >
                  Despublicar
                </LaisPrimaryButton>
              </div>
            </div>
          </LaisCard>

          <LaisCard title="Preview responsivo" density="flush">
            <div class="grid gap-4 p-4">
              <div class="rounded-lais border border-lais-border bg-white p-4">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p
                      class="text-[11px] font-semibold uppercase text-lais-text-muted"
                    >
                      Desktop
                    </p>
                    <h2 class="mt-1 text-[18px] font-semibold text-lais-text">
                      {{ shopWindowName }}
                    </h2>
                    <p class="text-[12px] text-lais-text-muted">
                      {{ agentName }} · {{ businessContext }}
                    </p>
                  </div>
                  <span
                    class="rounded-full px-3 py-1 text-[11px] font-semibold"
                    :class="
                      published
                        ? 'bg-lais-mint/20 text-[#178565]'
                        : 'bg-amber-50 text-amber-800 ring-1 ring-amber-200'
                    "
                  >
                    {{ published ? 'ao vivo' : 'rascunho' }}
                  </span>
                </div>

                <div
                  v-if="!published"
                  class="mt-4 rounded-lais border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] leading-5 text-amber-900"
                  role="status"
                >
                  Esta vitrine ainda é um rascunho. O link público pode estar
                  indisponível até a publicação.
                </div>

                <div
                  v-if="!selectedImoveis.length"
                  class="mt-4 rounded-lais border border-dashed border-lais-border px-4 py-8 text-center"
                >
                  <span
                    class="i-lucide-store mx-auto block size-7 text-lais-text-muted"
                    aria-hidden="true"
                  />
                  <p class="mt-2 text-[13px] font-semibold text-lais-text">
                    Vitrine sem imóveis
                  </p>
                  <p class="mt-1 text-[12px] text-lais-text-muted">
                    O preview público mostra este fallback até a seleção ser
                    salva.
                  </p>
                </div>

                <div v-else class="mt-4 grid gap-3 sm:grid-cols-2">
                  <article
                    v-for="imovel in selectedImoveis.slice(0, 4)"
                    :key="`preview-${imovel.id}`"
                    class="rounded-lais border border-lais-border bg-[#fcfaf6] p-3"
                  >
                    <p
                      class="line-clamp-2 text-[13px] font-semibold text-lais-text"
                    >
                      {{ propertyTitle(imovel) }}
                    </p>
                    <p class="mt-1 text-[12px] text-lais-text-muted">
                      {{
                        [imovel.bairro, imovel.cidade]
                          .filter(Boolean)
                          .join(', ')
                      }}
                    </p>
                    <p class="mt-2 text-[13px] font-semibold text-lais-text">
                      {{
                        formatMoney(imovel.valorAluguel || imovel.valorVenda)
                      }}
                    </p>
                  </article>
                </div>
              </div>

              <div
                class="mx-auto w-full max-w-[260px] rounded-[28px] border border-lais-border bg-[#111827] p-2 shadow-lais-card"
              >
                <div class="min-h-[360px] rounded-[22px] bg-white p-3">
                  <p
                    class="text-[11px] font-semibold uppercase text-lais-text-muted"
                  >
                    Mobile
                  </p>
                  <h3 class="mt-2 text-[16px] font-semibold text-lais-text">
                    {{ shopWindowName }}
                  </h3>
                  <div
                    v-if="!selectedImoveis.length"
                    class="mt-8 text-center text-[12px] text-lais-text-muted"
                  >
                    Nenhum imóvel selecionado.
                  </div>
                  <div v-else class="mt-3 grid gap-2">
                    <div
                      v-for="imovel in selectedImoveis.slice(0, 3)"
                      :key="`mobile-${imovel.id}`"
                      class="rounded-lais border border-lais-border px-3 py-2"
                    >
                      <p
                        class="line-clamp-1 text-[12px] font-semibold text-lais-text"
                      >
                        {{ propertyTitle(imovel) }}
                      </p>
                      <p class="text-[11px] text-lais-text-muted">
                        {{
                          formatMoney(imovel.valorAluguel || imovel.valorVenda)
                        }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </LaisCard>
        </aside>
      </div>
    </div>

    <GoToAgentAreaButton
      @click="
        payload =>
          track('listing_agents_section_button_clicked', {
            click_origin: payload.source,
          })
      "
    />
  </section>

  <ShopWindowDialog
    :show="showShopWindowDialog"
    :shop-windows="shopWindows"
    :recent-shop-windows="shopWindows"
    :state="shopWindowDialogState"
    :is-loading="shopWindowDialogState === 'loading'"
    :blocked-agent-name="blockedHolderName"
    :marketplace-url="marketplaceUrl"
    @close="closeShopWindowDialog"
    @select="selectShopWindow"
    @create-new="
      closeShopWindowDialog();
      openCreateDialog('create');
    "
    @retry="selectShopWindow"
    @view-all="openPublicPreview"
    @open-key-holder="openKeyHolderDrawer(pendingImovel)"
  />

  <CreateShopWindowDialog
    :show="showCreateDialog"
    variant="shop_window"
    :mode="createDialogMode"
    :initial-name="currentShopWindowId ? shopWindowName : ''"
    :state="createDialogState"
    :is-loading="isSaving"
    @close="showCreateDialog = false"
    @submit="submitCreateDialog"
    @retry="createDialogState = 'form'"
    @go-to-overview="showCreateDialog = false"
    @view-detail="openPublicPreview"
    @continue-search="showCreateDialog = false"
  />

  <AllReadyDialog
    :show="showAllReadyDialog"
    :agent="{ name: agentName }"
    @close="showAllReadyDialog = false"
    @chat-with-agent="openPublicPreview"
    @check-my-agents="showAllReadyDialog = false"
    @browse-imoveis="goToImoveis"
    @open-profiling="showAllReadyDialog = false"
  />

  <LaisConfirmModal
    :show="showUnpublishConfirm"
    title="Despublicar vitrine"
    description="A vitrine deixará de aparecer publicamente até ser publicada novamente."
    confirm-text="Despublicar"
    cancel-text="Manter publicada"
    :is-loading="isSaving"
    @close="showUnpublishConfirm = false"
    @confirm="unpublishShopWindow"
  />

  <KeyHolderDrawer
    :show="keyHolderDrawer.show"
    :title="keyHolderDrawer.title"
    :description="keyHolderDrawer.description"
    :holder-name="keyHolderDrawer.holderName"
    :property-title="
      keyHolderDrawer.imovel ? propertyTitle(keyHolderDrawer.imovel) : ''
    "
    :reason="keyHolderDrawer.reason"
    :role="keyHolderDrawer.role"
    :current-profile-role="keyHolderDrawer.currentProfileRole"
    :validation-enabled="keyHolderDrawer.validationEnabled"
    :keys-with-agent="keyHolderDrawer.keysWithAgent"
    :blocked-by-key-holder="keyHolderDrawer.blockedByKeyHolder"
    :source="keyHolderDrawer.source"
    :drawer-component="KEY_HOLDER_DRAWER_COMPONENT"
    :drawer-anchor="KEY_HOLDER_DRAWER_ANCHOR"
    @close="keyHolderDrawer.show = false"
    @go-to-visits="goToVisits"
  />
</template>
